import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { PoliciesService } from '../../service/policies.service';
import { NotificationService } from '../../service/notification.service';
import { LoadingService } from '../../service/loading.service';
import { applyBrandTheme } from '../../service/brand-theme';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { environment } from '../../../environments/environment.active';
import {
  CategoryChip,
  PortalDocument,
  categoryChips,
  clientInitials,
  documentSlugs,
  filterDocuments,
  formatUpdated,
  groupByCategory,
  recentlyUpdated,
  toPortalLibrary,
} from './portal-library';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterOutlet, NgxExtendedPdfViewerModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
})
export class HomeComponent implements OnInit {
  documents: PortalDocument[] = [];
  searchText = '';
  category = 'All';
  view: 'list' | 'grouped' = 'list';
  tab: 'all' | 'recent' = 'all';
  selected: PortalDocument | null = null;
  collapsedGroups = new Set<string>();
  readonly portalLabel = 'Policies and Procedures';
  clientName = environment.clientName;
  logoUrl = environment.logoUrl;
  pdfSrc: string | null = null;
  loadError = '';
  private documentBySlug = new Map<string, PortalDocument>();
  private libraryLoaded = false;
  private ownedPdfUrl: string | null = null;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly policiesService: PoliciesService,
    private readonly notificationService: NotificationService,
    private readonly spinner: LoadingService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncFromUrl());
    this.destroyRef.onDestroy(() => this.setPdfSrc(null));
    this.loadLibrary();
    this.loadClient();
  }

  get chips(): CategoryChip[] {
    return categoryChips(
      filterDocuments(this.documents, {
        search: '',
        category: 'All',
        recentOnly: this.tab === 'recent',
      }),
    );
  }

  get visibleDocuments(): PortalDocument[] {
    return filterDocuments(this.documents, {
      search: this.searchText,
      category: this.category,
      recentOnly: this.tab === 'recent',
    });
  }

  get groups() {
    return groupByCategory(this.visibleDocuments);
  }

  get recentDocuments(): PortalDocument[] {
    return recentlyUpdated(this.documents);
  }

  get initials(): string {
    return clientInitials(this.clientName);
  }

  updatedLabel(document: PortalDocument, style: 'long' | 'short' = 'long'): string {
    return formatUpdated(document.updatedAt, style);
  }

  metaLine(document: PortalDocument): string {
    const parts = [document.versionLabel, this.updatedLabel(document) ? `Updated ${this.updatedLabel(document)}` : '']
      .filter(Boolean);
    return parts.join(' · ');
  }

  selectCategory(name: string): void {
    this.category = name;
    this.view = 'list';
  }

  openDocument(document: PortalDocument): void {
    const slug = this.slugFor(document);
    this.showDocument(document);
    if (slug && this.currentSlug() !== slug) {
      void this.router.navigate(['/policies', slug]).catch(() => undefined);
    }
  }

  closeDetail(): void {
    this.clearDocument();
    if (this.currentSlug()) {
      void this.router.navigate(['/']).catch(() => undefined);
    }
  }

  download(document: PortalDocument, event?: Event): void {
    event?.stopPropagation();
    if (document.type === 'File' && document.fileUrl) {
      window.open(`${environment.apiUrl}${document.fileUrl}`, '_blank');
      return;
    }
    if (!document.formId) return;

    this.spinner.OnLoading.emit(true);
    const request =
      document.type === 'Procedure'
        ? this.policiesService.exportProcedurePdf(document.formId)
        : this.policiesService.exportPolicyPdf(document.formId);
    request.subscribe({
      next: (data: Blob) => {
        this.spinner.OnLoading.emit(false);
        const url = URL.createObjectURL(data);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = `${document.name}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: (err: any) => {
        this.spinner.OnLoading.emit(false);
        this.notificationService.showError(err, 'Error');
      },
    });
  }

  toggleGroup(name: string): void {
    if (this.collapsedGroups.has(name)) {
      this.collapsedGroups.delete(name);
    } else {
      this.collapsedGroups.add(name);
    }
  }

  isGroupOpen(name: string): boolean {
    return !this.collapsedGroups.has(name);
  }

  private loadLibrary(): void {
    this.spinner.OnLoading.emit(true);
    this.policiesService.getPolicies().subscribe({
      next: (data: any[]) => {
        this.spinner.OnLoading.emit(false);
        this.documents = toPortalLibrary(data);
        this.documentBySlug = documentSlugs(this.documents);
        this.libraryLoaded = true;
        this.loadError = '';
        if (
          this.category !== 'All' &&
          !this.documents.some((document) => document.categories.indexOf(this.category) !== -1)
        ) {
          this.category = 'All';
        }
        this.syncFromUrl();
      },
      error: (err: any) => {
        this.spinner.OnLoading.emit(false);
        this.loadError = 'The policy library could not be loaded.';
      },
    });
  }

  private loadClient(): void {
    this.policiesService.getPoliciesConfigurations().subscribe({
      next: (data: any) => {
        if (data?.name) this.clientName = data.name;
        if (data?.entityLogo && !environment.logoUrl) this.logoUrl = data.entityLogo;
        applyBrandTheme(data?.brandColor);
      },
      error: () => {
        this.clientName = environment.clientName;
        this.logoUrl = environment.logoUrl;
      },
    });
  }

  private showDocument(document: PortalDocument): void {
    if (this.selected === document) return;
    this.loadError = '';
    this.selected = document;
    this.setPdfSrc(null);
    if (document.type === 'File' && document.fileUrl) {
      this.setPdfSrc(`${environment.apiUrl}${document.fileUrl}`);
    } else if (document.formId) {
      this.loadPdf(document, false);
    }
    window.scrollTo({ top: 0 });
  }

  private clearDocument(): void {
    this.selected = null;
    this.setPdfSrc(null);
  }

  private syncFromUrl(): void {
    const slug = this.currentSlug();
    if (!slug) {
      if (this.selected) this.clearDocument();
      return;
    }
    const document = this.findDocument(slug);
    if (document) {
      this.showDocument(document);
      return;
    }
    if (!this.libraryLoaded) return;
    this.clearDocument();
    this.loadError = 'That policy is not on this portal.';
  }

  private findDocument(slug: string): PortalDocument | undefined {
    return (
      this.documentBySlug.get(slug) ??
      this.documents.find((document) => document.formId === slug || document.id === slug)
    );
  }

  private currentSlug(): string | null {
    const child = this.route.firstChild?.snapshot.paramMap.get('slug');
    const raw = child ?? this.router.url.match(/(?:^|\/)policies\/([^/?#]+)/)?.[1] ?? null;
    if (!raw) return null;
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  private slugFor(document: PortalDocument): string | null {
    let found: string | null = null;
    this.documentBySlug.forEach((item, slug) => {
      if (item === document) found = slug;
    });
    return found;
  }

  private setPdfSrc(url: string | null): void {
    if (this.ownedPdfUrl) {
      URL.revokeObjectURL(this.ownedPdfUrl);
      this.ownedPdfUrl = null;
    }
    if (url && url.startsWith('blob:')) this.ownedPdfUrl = url;
    this.pdfSrc = url;
  }

  private loadPdf(document: PortalDocument, downloadOnly: boolean): void {
    if (!document.formId) return;
    const request =
      document.type === 'Procedure'
        ? this.policiesService.exportProcedurePdf(document.formId)
        : this.policiesService.exportPolicyPdf(document.formId);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: Blob) => {
        if (!downloadOnly && this.selected === document) {
          this.setPdfSrc(URL.createObjectURL(data));
        }
      },
      error: (err: any) => this.notificationService.showError(err, 'Error'),
    });
  }
}
